<template>
    <div class="user-individual">
        <!-- First name -->
        <div class="app-input-group" :class="{ 'app-invalid-group': firstNameError }">
            <label for="userFirstName">First name</label>
            <input v-if="!isModify"
                   type="text"
                   id="userFirstName"
                   v-model="myFirsName"
                   @input="validateFirstName()"
                   aria-describedby="firstNameDesc"
                   :class="{ 'disabled': isModify }"
                   :readonly="isModify"
                   :aria-disabled="isModify" />
            <span v-else>{{ maskNamess(myFirsName || '') }}</span>
            <p>{{ firstNameError }}</p>
            <p id="firstNameDesc" class="sr-only">Optional. Up to 40 characters. Do not use comma, semicolon, less-than sign or pipe.</p>
        </div>

        <!-- Middle name -->
        <div class="app-input-group" :class="{ 'app-invalid-group': middleNameError }">
            <label for="userMiddleName">Middle name</label>
            <input v-if="!isModify"
                   type="text"
                   id="userMiddleName"
                   v-model="myMiddleName"
                   @input="validateMiddleName()"
                   aria-describedby="middleNameDesc"
                   :class="{ 'disabled': isModify }"
                   :readonly="isModify"
                   :aria-disabled="isModify" />
            <span v-else>{{ maskNamess(myMiddleName || '') }}</span>
            <p>{{ middleNameError }}</p>
            <p id="middleNameDesc" class="sr-only">Optional. Up to 35 characters. Do not use comma, semicolon, less-than sign or pipe.</p>
        </div>

        <!-- Last name -->
        <div class="app-input-row">
            <div class="app-input-group" :class="{ 'app-invalid-group': lastNameError }">
                <label for="userLastName" class="app-required-indicator __after">Last name</label>
                <input v-if="!isModify"
                       type="text"
                       id="userLastName"
                       v-model="myLastName"
                       @input="validateLastName()"
                       aria-describedby="lastNameDesc"
                       :class="{ 'disabled': isModify }"
                       :readonly="isModify"
                       :aria-disabled="isModify" />
                <span v-else>{{ maskNamess(myLastName || '') }}</span>
                <p>{{ lastNameError }}</p>
                <p id="lastNameDesc" class="sr-only">Required. Up to 40 characters. Do not use comma, semicolon, less-than sign or pipe.</p>
            </div>
        </div>

        <!-- Date of birth -->
        <div class="app-input-group" :class="{ 'app-invalid-group': dobError }">
            <label for="userDobText" class="app-required-indicator __after">
                Date of birth (MM/DD/YYYY)
            </label>

            <!-- visible text field for manual entry -->
            <div v-if="!isModify" class="dob-input-wrapper" style="position:relative; display:inline-block;">
                <input ref="dobTextInput"
                       type="text"
                       id="userDobText"
                       v-model="myDob"
                       @input="onDobTextInput"
                       placeholder="MM/DD/YYYY"
                       aria-describedby="dobDesc"
                       :class="{ 'disabled': isModify }"
                       :readonly="isModify"
                       :aria-disabled="isModify" />

                <!-- calendar icon and hidden picker only if browser supports native date control -->
                <template v-if="isDateSupported">
                    <span class="dob-calendar-icon"
                          tabindex="0"
                          role="button"
                          aria-label="Open calendar"
                          @click="openCalendar"
                          @touchend.prevent="openCalendar"
                          @keydown.enter.prevent="openCalendar"
                          @keydown.space.prevent="openCalendar"
                          style="position:absolute; right:4px; top:50%; transform:translateY(-50%); cursor:pointer; -webkit-tap-highlight-color: transparent; user-select: none;">
                        <img src="@ridmv/web-themes/images/icons/calendar-solid.svg" alt="" style="width:1em; height:1em; pointer-events: none;" />
                    </span>

                    <!-- off‑screen date input used to trigger native calendar picker -->
                    <input ref="dobPicker"
                           type="date"
                           :max="todayDate"
                           tabindex="-1"
                           aria-hidden="true"
                           @change="onDatePickerInput"
                           @blur="onDatePickerBlur"
                           style="position:absolute; left:-9999px; top:-9999px; width:1px; height:1px; opacity:0; visibility:hidden; pointer-events:none;" />
                </template>
            </div>

            <!-- show masked dob when in modify mode -->
            <span v-else>{{ getHiddenValue(getPrettyDob(myDob || '')) }}</span>
            <p :style="dobError ? { color: 'var(--ri-red)', width: 'var(--control-width)', 'font-weight': '400' } : {}">{{ dobError }}</p>
            <p id="dobDesc" class="sr-only">Required in MM/DD/YYYY format.</p>
        </div>

        <div class="app-input-row">
            <!-- License/Taxation ID -->
            <template v-if="resTypeForCustomer && (resTypeForCustomer.visitCategoryId == 7)">
                <div class="app-input-group" :class="{ 'app-invalid-group': licenseError }" rules="fieldRequired|alpha_num|max:11">
                    <div v-if="reservationSlotId > 0 && customerDetail">
                        <label for="riTaxationIdNumModify" class="app-required-indicator __after">Notice ID from Tax Verification Form</label>
                        <input v-if="ocaIsModify"
                               type="text"
                               id="riTaxationIdNumModify"
                               aria-describedby="riTaxationIdNumDescModify"
                               v-model="myLicense"
                               @input="validateLicense()"
                               @keydown.space.prevent />
                        <span v-else>{{ getHiddenValue(myLicense || '') }}</span>

                        <p>{{ licenseError }}</p>
                        <p id="riTaxationIdNumDescModify" class="sr-only">Required. Up to 11 alphanumeric characters only.</p>
                    </div>

                    <div v-else>
                        <label for="riTaxationIdNum" class="app-required-indicator __after">Notice ID from Tax Verification Form</label>
                        <input v-if="!isModify"
                               type="text"
                               id="riTaxationIdNum"
                               aria-describedby="riTaxationIdNumDesc"
                               v-model="myLicense"
                               @input="validateLicense()"
                               @keydown.space.prevent
                               :class="{ 'disabled': isModify }"
                               :readonly="isModify"
                               :aria-disabled="isModify" />
                        <span v-else>{{ getHiddenValue(myLicense || '') }}</span>

                        <p>{{ licenseError }}</p>
                        <p id="riTaxationIdNumDesc" class="sr-only">Required. Up to 11 alphanumeric characters only.</p>
                    </div>
                </div>
            </template>

            <template v-else>
                <div class="app-input-group" :class="{ 'app-invalid-group': licenseError }" :rules="licenseNumRules">
                    <label for="riLicense" :class="{ 'app-required-indicator __after': resFeeApplicable }">RI license number</label>
                    <input v-if="!isModify"
                           type="text"
                           id="riLicense"
                           aria-describedby="riLicenseDesc"
                           :required="resFeeApplicable"
                           v-model="myLicense"
                           @input="validateLicense()"
                           @keydown.space.prevent
                           :class="{ 'disabled': isModify }"
                           :readonly="isModify"
                           :aria-disabled="isModify" />
                    <span v-else>{{ getHiddenValue(myLicense || '') }}</span>
                    <p>{{ licenseError }}</p>
                    <p id="riLicenseDesc" class="sr-only">
                        {{ resFeeApplicable ? 'Required' : 'Optional' }}. Up to 8 alphanumeric characters only.
                    </p>
                </div>
            </template>

            <!-- License plate -->
            <div hidden class="app-input-group" :class="{ 'app-invalid-group': plateError }" rules="alpha_num|max:6">
                <label for="userPlate">License plate number</label>
                <input v-if="!isModify"
                       type="text"
                       id="userPlate"
                       @keydown.space.prevent
                       v-model="myPlate"
                       @input="validatePlate()"
                       aria-describedby="plateDesc"
                       :class="{ 'disabled': isModify }"
                       :readonly="isModify"
                       :aria-disabled="isModify" />
                <span v-else>{{ getHiddenValue(myPlate || '') }}</span>
                <p>{{ plateError }}</p>
                <p id="plateDesc" class="sr-only">Optional. Up to 6 alphanumeric characters only.</p>
            </div>

        </div>

        <!-- Contact component -->
        <div v-if="(reservationSlotId > 0 && customerDetail) || !useMfa">
            <UserContactOca :verify="verify" @doContactValidated="setIndividualContact" @doResetVerify="resetVerify" verifyDetails="" />
        </div>
        <div v-else>
            <UserContact :verify="verify" @doContactValidated="setIndividualContact" @doResetVerify="resetVerify" verifyDetails="" />
        </div>
    </div>
</template>


<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import UserContact from '@/components/reservation/user/UserContact.vue';
import UserContactOca from '@/components/reservation/user/UserContactOca.vue';
import { stripExtraSpaces } from '@/utils/utils';
import { confirmationMixin } from '@/utils/confirmationMixin';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter';
import { toDate, parse, isAfter } from 'date-fns';


// Props and Emits
const props = defineProps({
  verify: {
    type: Boolean,
    required: true,
  },
});
const emit = defineEmits(['doResetVerify', 'doScroll', 'doValidated']);

// Composables
const { formatPhoneForRims } = usePhoneFormatter();
const { getPrettyDob, getHiddenValue } = confirmationMixin();

// Reactive State
const store = useStore();
const myFirsName = ref('');
const myMiddleName = ref('');
const myLastName = ref('');
const myDob = ref('');
const myLicense = ref('');
const myPlate = ref('');
const myContact = ref(undefined);
const dobPicker = ref(null);
const dobTextInput = ref(null);
const isDateSupported = ref(true);
const isModify = ref(false);
const useMfa = ref(window.config.USEMFA);
const todayDate = ref('');

// Error message refs
const firstNameError = ref('');
const middleNameError = ref('');
const lastNameError = ref('');
const dobError = ref('');
const licenseError = ref('');
const plateError = ref('');

// Computed properties
const licenseNumRules = computed(() => {
  return {
    fieldRequired: store.state.user.resFeeApplicable,
    alpha_num: true,
    minMax: [7, 8],
  };
});

const resFeeApplicable = computed(() => store.state.user.resFeeApplicable);
const resTypeForCustomer = computed(() => store.state.user.resTypeForCustomer);
const reservationSlotId = computed(() => store.state.user.reservationSlotId);
const resDetails = computed(() => store.state.user.resDetails);
const customerDetail = computed(() => store.state.user.customerDetail);
const ocaIsModify = computed(() => store.state.ocaIsModify);

// Watchers
watch(() => props.verify, async (newVal) => {
  if (newVal) {
    validateFirstName();
    validateMiddleName();
    validateLastName();
    validateDob();
    validateLicense();
    validatePlate();
  }
});

// Lifecycle hooks
onMounted(async () => {
  setDateField();
  
  // set today's date in ISO format for calendar max
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  todayDate.value = `${year}-${month}-${day}`;

  if (reservationSlotId.value > 0 && resDetails.value.userInfo) {
    const userDetails = resDetails.value.userInfo;
    myFirsName.value = userDetails.firstName || '';
    myMiddleName.value = userDetails.middleName || '';
    myLastName.value = userDetails.lastName || '';
    myDob.value = trimDob(userDetails.dateOfBirth) || '';
    myLicense.value = userDetails.licenseNumber || '';
    myPlate.value = userDetails.plateNumber || '';
  }

  if (reservationSlotId.value > 0 && customerDetail.value) {
    const userDetails = customerDetail.value;
    myFirsName.value = userDetails.firstName || '';
    myMiddleName.value = userDetails.middleName || '';
    myLastName.value = userDetails.lastName || '';
    myDob.value = trimDob(userDetails.dateOfBirth) || '';

    const isVisitType = resTypeForCustomer.value && [35, 36, 37].includes(resTypeForCustomer.value.visitTypeId);

    if (isVisitType && ocaIsModify.value) {
      myLicense.value = '';
    } else if ( reservationSlotId.value > 0 && resDetails.value.userInfo && isVisitType) {
      myLicense.value = resDetails.value.userInfo.licenseNumber || '';
    } else {
      myLicense.value = userDetails.licenseNumber || '';
    }
    myPlate.value = userDetails.plateNumber || '';
  }

  isModify.value = await store.dispatch('user/isReservationForUpdate');
  useMfa.value = window.config.USEMFA;

  if (reservationSlotId.value > 0 && customerDetail.value) {
    isModify.value = true;
  }
});

// Methods
const resetVerify = () => {
  emit('doResetVerify');
};

const scrollToError = () => {
  emit('doScroll');
};

const setIndividualContact = (data) => {
  myContact.value = data;
  validateIndividual();
};

const setDateField = () => {
  const input = document.createElement('input');
  input.setAttribute('type', 'date');
  input.setAttribute('value', 'not-a-date');
  // If the browser supports type="date", it rejects invalid values → value is ''
  // If it doesn't support it (treats as text), value stays as 'not-a-date'
  isDateSupported.value = input.value !== 'not-a-date';
};

const reformatDob = (dt) => {
  if ((dt.match(new RegExp(/^\d{4}-\d{2}-\d{2}.*/, 'g')) || []).length === 1) {
    return `${dt.substr(5, 2)}/${dt.substr(8, 2)}/${dt.substr(0, 4)}`;
  }
  return dt;
};

// keep hidden picker in sync when user types manually
watch(myDob, (newVal) => {
  if (dobPicker.value) {
    const m = newVal.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (m) {
      dobPicker.value.value = `${m[3]}-${m[1]}-${m[2]}`;
    } else {
      dobPicker.value.value = newVal;
    }
  }
});

const openCalendar = (e) => {
  if (!dobPicker.value || !dobTextInput.value) {
    return;
  }

  // Prevent default to avoid any browser quirks
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }

  // For mobile devices, use a simpler approach
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // Simply make the date picker visible and trigger it immediately
    dobPicker.value.style.position = 'fixed';
    dobPicker.value.style.left = '50%';
    dobPicker.value.style.top = '50%';
    dobPicker.value.style.transform = 'translate(-50%, -50%)';
    dobPicker.value.style.width = '200px';
    dobPicker.value.style.height = '50px';
    dobPicker.value.style.opacity = '1';
    dobPicker.value.style.visibility = 'visible';
    dobPicker.value.style.pointerEvents = 'auto';
    dobPicker.value.style.zIndex = '9999';

    // Focus first, then trigger
    dobPicker.value.focus();
    
    // Use requestAnimationFrame to ensure the focus has taken effect
    requestAnimationFrame(() => {
      if (typeof dobPicker.value.showPicker === 'function') {
        try {
          dobPicker.value.showPicker();
        } catch (err) {
          // Fallback for browsers that don't support showPicker
          dobPicker.value.click();
        }
      } else {
        dobPicker.value.click();
      }
    });
  } else {
    // Desktop approach - make input properly visible and ensure picker opens
    const rect = dobTextInput.value.getBoundingClientRect();
    
    // Temporarily make the input visible and properly sized
    dobPicker.value.style.position = 'fixed';
    dobPicker.value.style.left = `${rect.left}px`;
    dobPicker.value.style.top = `${rect.top}px`;
    dobPicker.value.style.width = `${rect.width}px`;
    dobPicker.value.style.height = `${rect.height}px`;
    dobPicker.value.style.opacity = '1';
    dobPicker.value.style.visibility = 'visible';
    dobPicker.value.style.pointerEvents = 'auto';
    dobPicker.value.style.zIndex = '10000';
    dobPicker.value.style.cursor = 'pointer';

    // Ensure element is properly focused and clickable
    dobPicker.value.focus();
    
    // Try multiple methods to open the picker
    setTimeout(() => {
      try {
        if (typeof dobPicker.value.showPicker === 'function') {
          dobPicker.value.showPicker();
        } else {
          // Fallback: directly invoke click
          dobPicker.value.click();
        }
      } catch (err) {
        console.warn('Error opening date picker:', err);
        // Final fallback
        dobPicker.value.click();
      }
    }, 50);
  }
};
    // called when user types manually in the text input
const onDobTextInput = (e) => {
  // auto-format as MM/DD/YYYY while typing
  let v = e.target.value || '';
  // strip anything that's not a digit
  const digits = v.replace(/[^\d]/g, '');
  if (digits.length <= 2) {
    v = digits;
  } else if (digits.length <= 4) {
    v = `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    v = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
  myDob.value = v;

  validateDob();
};

// called when native date picker (hidden) emits a value
const onDatePickerInput = (e) => {
  let val = e.target.value;
  if (val) {
    const parts = val.split('-');
    if (parts.length === 3) {
      val = `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
  }
  myDob.value = val;
  validateDob();
  
  // Hide the picker after selection
  if (dobPicker.value) {
    dobPicker.value.style.cssText = 'position:absolute; left:-9999px; top:-9999px; width:1px; height:1px; opacity:0; visibility:hidden; pointer-events:none;';
    dobPicker.value.blur();
  }
};

const onDatePickerBlur = () => {
  // Hide the picker when user closes it (e.g., by tapping outside or pressing cancel)
  if (dobPicker.value) {
    setTimeout(() => {
      if (dobPicker.value) {
        dobPicker.value.style.cssText = 'position:absolute; left:-9999px; top:-9999px; width:1px; height:1px; opacity:0; visibility:hidden; pointer-events:none;';
      }
    }, 200);
  }
};

const trimDob = (dt) => {
  const dob = (dt && !isDateSupported.value) ? reformatDob(dt) : dt;
  const t = dob ? dob.indexOf('T') : dob;
  if (t > -1) {
    return dob.substr(0, t);
  }
  return dob;
};

const validateFirstName = () => {
  firstNameError.value = '';
  if (myFirsName.value && /[;,<|]/.test(myFirsName.value)) {
    firstNameError.value = 'Special characters ";", "<", "|", "," and "-" are not allowed. For hyphenated names please use a space in place of hyphen / dash.';
  } else if (myFirsName.value && myFirsName.value.length > 40) {
    firstNameError.value = 'First name is limited to 40 characters.';
  }
  return !firstNameError.value;
};

const validateMiddleName = () => {
  middleNameError.value = '';
  if (myMiddleName.value && /[;,<|]/.test(myMiddleName.value)) {
    middleNameError.value = 'Special characters ";", "<", "|", "," and "-" are not allowed. For hyphenated names please use a space in place of hyphen / dash.';
  } else if (myMiddleName.value && myMiddleName.value.length > 35) {
    middleNameError.value = 'Middle name is limited to 35 characters.';
  }
  return !middleNameError.value;
};

const validateLastName = () => {
  lastNameError.value = '';
  if (!myLastName.value) {
    lastNameError.value = 'Last name is required.';
  } else if (myLastName.value.length > 40) {
    lastNameError.value = 'Last name is limited to 40 characters.';
  } else if (/[;,<|]/.test(myLastName.value)) {
    lastNameError.value = 'Special characters ";", "<", "|", "," and "-" are not allowed. For hyphenated names please use a space in place of hyphen / dash.';
  }
  return !lastNameError.value;
};

const validateDob = () => {
  dobError.value = '';
  let value = myDob.value;

  if (!value) {
    dobError.value = 'A valid date of birth is required.';
    return false;
  }

  // Show error for input while typing 
  if (value.length != 10) {
    dobError.value = 'Date must be within the acceptable range.';
    return false;
  }

  // if ISO format slipped in, convert to MM/DD/YYYY
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    value = `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
    myDob.value = value;
  }

  // Check format is MM/DD/YYYY
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    dobError.value = 'A valid date of birth is required.';
    return false;
  }

  // parse using known format
  const parsed = parse(value, 'MM/dd/yyyy', new Date());
  if (Number.isNaN(parsed.getTime())) {
    dobError.value = 'A valid date of birth is required.';
    return false;
  }

  const minDate = toDate(new Date(1751, 11, 31, 23, 59, 59));
  if (!isAfter(parsed, minDate)) {
    dobError.value = 'Date must be within the acceptable range.';
    return false;
  }

  // Future DOB not allowed
  const today = new Date();
  today.setHours(23, 59, 59, 999); // Set to end of day to allow today's date
  if (isAfter(parsed, today)) {
    dobError.value = 'A valid date of birth is required.';
    return false;
  }

  // Clear error if all validations pass
  dobError.value = '';
  return true;
};

const validateLicense = () => {
  licenseError.value = '';
  const visitTypeId = resTypeForCustomer.value?.visitTypeId;
  const isTaxVerification = visitTypeId === 35 || visitTypeId === 36 || visitTypeId === 37;
  const value = myLicense.value

  if (isTaxVerification) {
    if (!value) {
      licenseError.value = 'Notice ID from Tax Verification Form is required.';
    } else if (!/^[a-zA-Z0-9]*$/.test(value)) {
      licenseError.value = 'Only alphanumeric characters are allowed.';
    } else if (value.length > 11) {
      licenseError.value = 'Notice ID from Tax Verification Form is limited to 11 characters.';
    }
  } 
  else if (resFeeApplicable.value && !value) {
    licenseError.value = 'RI license number is required.';
  } else if (value && value.length > 8) {
    licenseError.value = 'RI license number must have 7 or 8 characters.';
  } else if (value && !/^[a-zA-Z0-9]*$/.test(value)) {
    licenseError.value = 'Up to 8 alphanumeric characters only.';
  }
  return !licenseError.value;
};

const validatePlate = () => {
  plateError.value = '';
  if (myPlate.value && (myPlate.value.length > 6 || !/^[a-zA-Z0-9]*$/.test(myPlate.value))) {
    plateError.value = 'Up to 6 alphanumeric characters only.';
  }
  return !plateError.value;
};

const validateIndividual = async () => {
  const firstNameValid = validateFirstName();
  const middleNameValid = validateMiddleName();
  const lastNameValid = validateLastName();
  const dobValid = validateDob();
  const licenseValid = validateLicense();
  const plateValid = validatePlate();

  if (props.verify && firstNameValid && middleNameValid && lastNameValid && dobValid && licenseValid && plateValid && myContact.value) {
    const data = {
      dateOfBirth: myDob.value,
      firstName: stripExtraSpaces(myFirsName.value),
      isBusinessCustomer: false,
      lastName: stripExtraSpaces(myLastName.value),
      licenseNumber: myLicense.value,
      middleName: stripExtraSpaces(myMiddleName.value),
      plateNumber: myPlate.value,
      reservationSendConfirmationRequest: {
        email: myContact.value ? myContact.value.email : '',
        isConsentProvided: myContact.value ? myContact.value.isConsentProvided : false,
        phone: myContact.value ? formatPhoneForRims(myContact.value.phone) : '',
        isModifyRequest: isModify.value,
        oneTimeOtp: myContact.value.oneTimeOtp,
        verifiedContactType: myContact.value?.verifiedContactType, // 'email' or 'phone' — whichever the user chose for OTP
      },
    };
    emit('doValidated', data);
  } else {
    scrollToError();
    resetVerify();
  }

};

const maskNamess = (name, visibleCharsStart = 1, visibleCharsEnd = 1, maskChar = '*') => {
  if (name.length <= visibleCharsStart + visibleCharsEnd) {
    return name;
  }
  const start = name.slice(0, visibleCharsStart);
  const end = name.slice(-visibleCharsEnd);
  const maskedSection = maskChar.repeat(name.length - visibleCharsStart - visibleCharsEnd);
  return `${start}${maskedSection}${end}`;
};

defineExpose({
  validateIndividual,
});
</script>